import React, { useEffect, useRef, useState } from 'react'
import { useSocket } from '../../contexts/SocketContext'
import { useNotifications } from '../../contexts/NotificationContext'

/**
 * CO-BROWSE VIEWER (Phase 1) — the live, view-only mirror of the visitor's page.
 *
 * Rendered by ChatThread when a consented session is active (large area, with
 * the chat docked beside it), and by the standalone /cobrowse-view/:chatId route
 * when the agent pops the mirror into its own window. It joins the cobrowse room
 * as an agent (which replays the buffered snapshot so the page renders
 * immediately) and feeds every rrweb event into a sandboxed live Replayer.
 * View-only — no input is ever sent back to the visitor.
 */

declare global {
  interface Window {
    rrweb?: any
  }
}

// Served by our API (same origin) — no CDN, so no dashboard CSP change.
const RRWEB_JS = '/api/widget/rrweb.min.js'
const RRWEB_CSS = '/api/widget/rrweb.min.css'

function loadRrweb(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.rrweb) return resolve()
    if (!document.querySelector(`link[href="${RRWEB_CSS}"]`)) {
      const l = document.createElement('link')
      l.rel = 'stylesheet'
      l.href = RRWEB_CSS
      document.head.appendChild(l)
    }
    const s = document.createElement('script')
    s.src = RRWEB_JS
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Failed to load the co-browse player'))
    document.head.appendChild(s)
  })
}

interface CobrowseViewerProps {
  chatId: string
  /** Visitor's display name, shown in the header. */
  visitorLabel?: string
  /** Called when the agent presses "End co-browse" (hidden in standalone mode). */
  onEnd?: () => void
  /** Called when the agent pops the view into its own window (session continues). */
  onPopOut?: () => void
  /** Full-window popped-out mode: no End button, offers Close window instead. */
  standalone?: boolean
}

export const CobrowseViewer: React.FC<CobrowseViewerProps> = ({
  chatId,
  visitorLabel,
  onEnd,
  onPopOut,
  standalone,
}) => {
  const { socket } = useSocket()
  const { addNotification } = useNotifications()
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [elapsed, setElapsed] = useState(0)
  // Phase 2 remote control (clicks + scroll only).
  const [control, setControl] = useState<'off' | 'requesting' | 'on'>('off')

  const containerRef = useRef<HTMLDivElement | null>(null)
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const replayerRef = useRef<any>(null)
  const scaledRef = useRef(false)
  const lastWidthRef = useRef(0)
  const scaleRef = useRef(1)

  // Elapsed timer.
  useEffect(() => {
    const t = setInterval(() => setElapsed((e) => e + 1), 1000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    if (!socket) return
    let cancelled = false
    // Incoming mirror events are buffered and flushed in an animation frame, so
    // the socket handler returns instantly. Otherwise heavy synchronous replay
    // work runs inside the socket callback and delays OTHER socket events on the
    // same connection — e.g. new chat messages arriving late while co-browsing.
    const pending: any[] = []
    let rafId: number | null = null
    let readySet = false

    const applyScale = (recordedWidth: number) => {
      if (!containerRef.current) return
      const avail = containerRef.current.clientWidth - 8
      const scale = Math.min(1, avail / recordedWidth)
      scaleRef.current = scale
      const wrapper = containerRef.current.querySelector('.replayer-wrapper') as HTMLElement | null
      if (wrapper) {
        wrapper.style.transform = `scale(${scale})`
        wrapper.style.transformOrigin = 'top left'
      }
    }

    const flush = () => {
      rafId = null
      if (cancelled || pending.length === 0) return
      const batch = pending.splice(0, pending.length)
      try {
        if (!replayerRef.current && window.rrweb && containerRef.current) {
          replayerRef.current = new window.rrweb.Replayer([], {
            liveMode: true,
            root: containerRef.current,
          })
          replayerRef.current.startLive(Date.now() - 300)
        }
        for (const e of batch) {
          // A single bad incremental event shouldn't drop the rest of the batch.
          try { replayerRef.current?.addEvent(e) } catch { /* skip, keep going */ }
          // Meta event (type 4) carries the recorded viewport — scale to fit.
          if (e?.type === 4 && containerRef.current) {
            scaledRef.current = true
            lastWidthRef.current = e.data?.width || 1280
            applyScale(lastWidthRef.current)
          }
        }
        if (!readySet) { readySet = true; setReady(true) }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[cobrowse] replay flush error (ignored)', e)
      }
    }

    const onEvent = (msg: any) => {
      if (cancelled || String(msg?.chatId) !== String(chatId)) return
      pending.push(msg.e)
      if (rafId == null) rafId = requestAnimationFrame(flush)
    }

    const setup = async () => {
      try {
        await loadRrweb()
      } catch (e: any) {
        setError(e?.message || 'Failed to load the co-browse player')
        return
      }
      if (cancelled) return
      socket.emit('cobrowse_join', { chatId: String(chatId), role: 'agent' })
      socket.on('cobrowse_event', onEvent)
    }
    setup()

    // Re-fit on window resize.
    const onResize = () => {
      if (scaledRef.current && lastWidthRef.current) applyScale(lastWidthRef.current)
    }
    window.addEventListener('resize', onResize)

    return () => {
      cancelled = true
      if (rafId != null) cancelAnimationFrame(rafId)
      window.removeEventListener('resize', onResize)
      socket.off('cobrowse_event', onEvent)
      socket.emit('cobrowse_leave', { chatId: String(chatId) })
      try {
        replayerRef.current?.pause?.()
      } catch {
        /* noop */
      }
      replayerRef.current = null
      scaledRef.current = false
      if (containerRef.current) containerRef.current.innerHTML = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, chatId])

  // ── Remote control (Phase 2) ───────────────────────────────────────────────
  const controlRef = useRef<'off' | 'requesting' | 'on'>('off')
  controlRef.current = control

  const requestControl = () => {
    if (!socket) return
    socket.emit('cobrowse_control_request', { chatId: String(chatId) })
    setControl('requesting')
  }
  const releaseControl = () => {
    if (socket) socket.emit('cobrowse_control_stop', { chatId: String(chatId) })
    setControl('off')
  }
  // Rebuild the mirror from the server's buffered snapshot (recovers a blank/
  // frozen view without ending the session).
  const reloadView = () => {
    try { replayerRef.current?.pause?.() } catch { /* noop */ }
    replayerRef.current = null
    scaledRef.current = false
    if (containerRef.current) containerRef.current.innerHTML = ''
    setReady(false)
    if (socket) socket.emit('cobrowse_join', { chatId: String(chatId), role: 'agent' })
  }

  // Control-lifecycle socket events.
  useEffect(() => {
    if (!socket) return
    const match = (d: any) => String(d?.chatId) === String(chatId)
    const onStarted = (d: any) => { if (match(d)) setControl('on') }
    const onDeclined = (d: any) => { if (match(d)) { setControl('off'); addNotification('The visitor declined remote control.', 'warning') } }
    const onTimeout = (d: any) => { if (match(d)) { setControl('off'); addNotification('Remote-control request timed out.', 'warning') } }
    const onCtlStopped = (d: any) => { if (match(d)) setControl('off') }
    const onStopped = (d: any) => { if (match(d)) setControl('off') }
    socket.on('cobrowse_control_started', onStarted)
    socket.on('cobrowse_control_declined', onDeclined)
    socket.on('cobrowse_control_timeout', onTimeout)
    socket.on('cobrowse_control_stopped', onCtlStopped)
    socket.on('cobrowse_stopped', onStopped)
    return () => {
      socket.off('cobrowse_control_started', onStarted)
      socket.off('cobrowse_control_declined', onDeclined)
      socket.off('cobrowse_control_timeout', onTimeout)
      socket.off('cobrowse_control_stopped', onCtlStopped)
      socket.off('cobrowse_stopped', onStopped)
    }
  }, [socket, chatId, addNotification])

  // While in control, capture the agent's pointer (press/move/release) + wheel
  // over the mirror and send them (as visitor client coords) so the visitor page
  // replays clicks, DRAGS and scrolls. A press+release without movement becomes a
  // click. Esc releases control.
  useEffect(() => {
    if (control !== 'on' || !socket) return
    const el = overlayRef.current
    const toVisitorCoords = (clientX: number, clientY: number) => {
      const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement | null
      if (!iframe) return null
      const r = iframe.getBoundingClientRect()
      const scale = scaleRef.current || 1
      return { x: (clientX - r.left) / scale, y: (clientY - r.top) / scale }
    }
    const send = (type: string, clientX: number, clientY: number, extra?: any) => {
      const p = toVisitorCoords(clientX, clientY)
      if (!p || p.x < 0 || p.y < 0) return
      socket.emit('cobrowse_control_event', {
        chatId: String(chatId),
        ev: { type, x: p.x, y: p.y, ...(extra || {}) },
      })
    }

    let down = false
    let moved = false
    let startX = 0
    let startY = 0
    let lastMove = 0

    const onDown = (e: MouseEvent) => {
      if (e.button !== 0) return
      e.preventDefault()
      down = true
      moved = false
      startX = e.clientX
      startY = e.clientY
      send('pointerdown', e.clientX, e.clientY)
    }
    const onMove = (e: MouseEvent) => {
      if (!down) return
      if (Math.abs(e.clientX - startX) > 3 || Math.abs(e.clientY - startY) > 3) moved = true
      const now = Date.now()
      if (now - lastMove < 33) return // ~30/s is plenty for a drag
      lastMove = now
      send('pointermove', e.clientX, e.clientY)
    }
    const onUp = (e: MouseEvent) => {
      if (!down) return
      down = false
      send('pointerup', e.clientX, e.clientY)
      if (!moved) send('click', e.clientX, e.clientY) // a tap → click
    }
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      send('scroll', e.clientX, e.clientY, { dx: e.deltaX, dy: e.deltaY })
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') releaseControl() }

    el?.addEventListener('mousedown', onDown)
    // moves/up on window so a drag that leaves the overlay still tracks.
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    el?.addEventListener('wheel', onWheel, { passive: false })
    window.addEventListener('keydown', onKey)
    return () => {
      // If control ends mid-drag, release on the visitor so their drag state
      // (e.g. the chat window) never stays stuck to the pointer.
      if (down) send('pointerup', startX, startY)
      el?.removeEventListener('mousedown', onDown)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      el?.removeEventListener('wheel', onWheel)
      window.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [control, socket, chatId])

  // Releasing control if the viewer unmounts mid-control (e.g. pop-out closed).
  useEffect(() => {
    return () => {
      if (controlRef.current === 'on' && socket) {
        socket.emit('cobrowse_control_stop', { chatId: String(chatId) })
      }
    }
  }, [socket, chatId])

  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0')
  const ss = String(elapsed % 60).padStart(2, '0')

  const popOut = () => {
    window.open(
      `/cobrowse-view/${encodeURIComponent(chatId)}`,
      `cobrowse_${chatId}`,
      'width=1200,height=800',
    )
    // Collapse the in-chat viewer but keep the session running in the new window.
    onPopOut?.()
  }

  return (
    <div className="flex flex-col h-full min-w-0 bg-surface-secondary">
      {/* Viewer header */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-border bg-surface">
        <div className="flex items-center gap-2 min-w-0">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
          <span className="text-sm font-medium text-txt-primary truncate">
            Viewing {visitorLabel || 'visitor'}&rsquo;s screen
          </span>
          <span className="text-xs text-txt-tertiary tabular-nums">
            {mm}:{ss}
          </span>
          <button
            onClick={reloadView}
            title="Refresh the live view (rebuilds from the latest snapshot)"
            aria-label="Refresh the live view"
            className="flex items-center justify-center w-6 h-6 rounded-md text-txt-tertiary hover:text-brand-600 hover:bg-surface-hover transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
            </svg>
          </button>

          {/* Remote control (clicks + scroll only) — kept on the LEFT, well away
              from End co-browse, so a mis-click/double-click can't end the session. */}
          <span className="mx-1 h-4 w-px bg-border" />
          {control === 'off' && (
            <button
              onClick={requestControl}
              title="Ask the visitor to let you click and scroll on their screen"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-brand-700 dark:text-brand-300 bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-800 hover:bg-brand-100 dark:hover:bg-brand-900/50 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59" />
              </svg>
              Request control
            </button>
          )}
          {control === 'requesting' && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <span className="w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              Requesting control…
              <button onClick={releaseControl} title="Cancel" className="ml-1 text-amber-600 hover:text-amber-800">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          )}
          {control === 'on' && (
            <button
              onClick={releaseControl}
              title="Release control (Esc)"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white bg-brand-600 hover:bg-brand-700 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672z" />
              </svg>
              In control · Release (Esc)
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!standalone && (
            <button
              onClick={popOut}
              title="Open the live view in its own window"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-txt-secondary border border-border hover:bg-surface-hover transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5h5v5m0-5L10 14M9 5H5a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4" />
              </svg>
              Pop out
            </button>
          )}
          {standalone ? (
            <button
              onClick={() => window.close()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-txt-secondary border border-border hover:bg-surface-hover transition-colors"
            >
              Close window
            </button>
          ) : (
            <button
              onClick={onEnd}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-error border border-error/30 hover:bg-error-light transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
              End co-browse
            </button>
          )}
        </div>
      </div>

      {/* Mirror */}
      <div className="relative flex-1 min-h-0 overflow-auto p-1">
        <div ref={containerRef} className="min-h-full" />
        {/* Control capture overlay — intercepts the agent's clicks + wheel and
            forwards them to the visitor's page. Only present while in control. */}
        {control === 'on' && (
          <div
            ref={overlayRef}
            className="absolute inset-0 z-10 cursor-crosshair ring-2 ring-inset ring-brand-500/60"
            title="You're controlling the visitor's screen — click and scroll. Esc to release."
          />
        )}
        {!ready && !error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pointer-events-none">
            <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-sm text-txt-secondary">Waiting for the visitor&rsquo;s screen…</p>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center text-center px-6">
            <p className="text-sm text-error">{error}</p>
          </div>
        )}
      </div>
    </div>
  )
}
